import { autoUpdate, flip, FloatingPortal, offset, shift, useFloating } from '@floating-ui/react'
import ctl from '@netlify/classnames-template-literals'
import { useEffect, useRef } from 'react'

export function QuickToolButton(
	{ active, onClick, title, children, buttonRef }: {
		active: boolean
		onClick: () => void
		title: string
		children: React.ReactNode
		buttonRef?: React.Ref<HTMLButtonElement>
	},
) {
	return (
		<button
			ref={buttonRef}
			type="button"
			onClick={onClick}
			title={title}
			className={ctl(`
				flex size-6 items-center justify-center rounded
				outline-none focus-visible:ring-1 focus-visible:ring-blue-400
				${active ? 'bg-blue-100 text-blue-600' : 'text-grey-500 hover:bg-grey-100 hover:text-grey-700'}
			`)}
		>
			{children}
		</button>
	)
}

export function QuickToolPopover(
	{ anchorEl, open, onClose, children }: {
		anchorEl: HTMLElement | null
		open: boolean
		onClose: () => void
		children: React.ReactNode
	},
) {
	const panelRef = useRef<HTMLDivElement | null>(null)

	const { refs, floatingStyles } = useFloating({
		placement: 'bottom',
		middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
		elements: { reference: anchorEl },
		whileElementsMounted: (...args) => autoUpdate(...args, { animationFrame: true }),
	})

	useEffect(() => {
		if (!open) { return }
		function handlePointerDown(e: PointerEvent) {
			const panel = panelRef.current
			if (panel && panel.contains(e.target as Node)) { return }
			if (anchorEl && anchorEl.contains(e.target as Node)) { return }
			onClose()
		}
		document.addEventListener('pointerdown', handlePointerDown)
		return () => document.removeEventListener('pointerdown', handlePointerDown)
	}, [open, anchorEl, onClose])

	if (!open || !anchorEl) { return null }

	return (
		<FloatingPortal>
			<div
				ref={(el) => {
					refs.setFloating(el)
					panelRef.current = el
				}}
				data-render-exclude
				style={{ ...floatingStyles, pointerEvents: 'auto' }}
				className="rounded-md border border-grey-200 bg-white shadow-lg"
			>
				{children}
			</div>
		</FloatingPortal>
	)
}
